export function parsePositiveInteger(value: string | null, fallback: number): number {
  const parsed = Number.parseInt(String(value || ""), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export function areStringArraysEqual(left?: string[], right?: string[]): boolean {
  return JSON.stringify(left || []) === JSON.stringify(right || []);
}

export function areSortEqual(
  left: { attribute: string; order: "ASC" | "DESC" }[],
  right: { attribute: string; order: "ASC" | "DESC" }[]
): boolean {
  return JSON.stringify(left || []) === JSON.stringify(right || []);
}

export interface QueueFilterSessionState {
  status?: string[];
  customer?: string[];
}

const QUEUE_FILTER_SESSION_STORAGE_PREFIX = "theo:supervisorQueueFilters:v1";

export function sanitizeFilterValues(values?: string[]): string[] | undefined {
  if (!Array.isArray(values)) {
    return undefined;
  }

  return values
    .map((value) => String(value || "").trim())
    .filter(Boolean);
}

export function getQueueFilterSessionStorageKey(
  userKey?: string,
  mspId?: number | string
): string | null {
  const normalizedUserKey = String(userKey || "").trim().toLowerCase();
  const normalizedMspId = String(mspId || "").trim();
  if (!normalizedUserKey || !normalizedMspId) {
    return null;
  }

  return `${QUEUE_FILTER_SESSION_STORAGE_PREFIX}:${normalizedMspId}:${normalizedUserKey}`;
}

export function readQueueFilterSessionState(
  storageKey: string | null
): QueueFilterSessionState {
  if (!storageKey || typeof window === "undefined") {
    return {};
  }

  try {
    const rawValue = window.sessionStorage.getItem(storageKey);
    if (!rawValue) {
      return {};
    }

    const parsed = JSON.parse(rawValue) as QueueFilterSessionState;
    return {
      status: sanitizeFilterValues(parsed.status),
      customer: sanitizeFilterValues(parsed.customer),
    };
  } catch {
    return {};
  }
}

export function writeQueueFilterSessionState(
  storageKey: string | null,
  state: QueueFilterSessionState
) {
  if (!storageKey || typeof window === "undefined") {
    return;
  }

  try {
    const nextState: QueueFilterSessionState = {
      status: sanitizeFilterValues(state.status),
      customer: sanitizeFilterValues(state.customer),
    };
    window.sessionStorage.setItem(storageKey, JSON.stringify(nextState));
  } catch {
    // Ignore storage failures so private browsing or quota issues do not break filtering.
  }
}

export function areQueueFilterSessionStatesEqual(
  left: QueueFilterSessionState,
  right: QueueFilterSessionState
) {
  return (
    areStringArraysEqual(left.status, right.status) &&
    areStringArraysEqual(left.customer, right.customer)
  );
}

export function parseQueueUrlState(
  search: string,
  defaultStatusFilter?: string[],
  sessionFilterState: QueueFilterSessionState = {}
): {
  status?: string[];
  customer?: string[];
  page: number;
  pageSize: number;
  sort: { attribute: string; order: "ASC" | "DESC" }[];
} {
  const params = new URLSearchParams(search);
  const hasExplicitStatusSelection = params.get("statusSet") === "1";
  const statusValues = params
    .getAll("status")
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  const hasExplicitCustomerSelection = params.get("customerSet") === "1";
  const customerValues = params
    .getAll("customer")
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  const sortAttribute = String(params.get("sortAttr") || "").trim();
  const sortOrder = String(params.get("sortOrder") || "DESC").toUpperCase() === "ASC"
    ? "ASC"
    : "DESC";

  return {
    status: hasExplicitStatusSelection
      ? statusValues
      : sessionFilterState.status ?? defaultStatusFilter,
    customer: hasExplicitCustomerSelection
      ? customerValues
      : sessionFilterState.customer,
    page: parsePositiveInteger(params.get("page"), 1),
    pageSize: parsePositiveInteger(params.get("pageSize"), 10),
    sort: [
      {
        attribute: sortAttribute || "created_at",
        order: sortOrder,
      },
    ],
  };
}

export function buildQueueSearch(params: {
  status?: string[];
  customer?: string[];
  page: number;
  pageSize: number;
  sort: { attribute: string; order: "ASC" | "DESC" }[];
}) {
  const searchParams = new URLSearchParams();

  if (params.status !== undefined) {
    searchParams.set("statusSet", "1");
    params.status.forEach((status) => {
      const normalized = String(status || "").trim();
      if (normalized) {
        searchParams.append("status", normalized);
      }
    });
  }

  if (params.customer !== undefined) {
    searchParams.set("customerSet", "1");
    params.customer.forEach((customer) => {
      const normalized = String(customer || "").trim();
      if (normalized) {
        searchParams.append("customer", normalized);
      }
    });
  }

  if (params.page > 1) {
    searchParams.set("page", String(params.page));
  }

  if (params.pageSize !== 10) {
    searchParams.set("pageSize", String(params.pageSize));
  }

  const [primarySort] = params.sort || [];
  if (
    primarySort &&
    (primarySort.attribute !== "created_at" || primarySort.order !== "DESC")
  ) {
    searchParams.set("sortAttr", primarySort.attribute);
    searchParams.set("sortOrder", primarySort.order);
  }

  const nextSearch = searchParams.toString();
  return nextSearch ? `?${nextSearch}` : "";
}

